import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Users, TrendingUp, TrendingDown, CalendarDays, Loader2, RefreshCw, Clock, Settings2, Star, Plus, X, AlertTriangle, Database, Zap, Sparkles } from "lucide-react";
import MarketUpdateControls from "@/components/MarketUpdateControls";

const STATUS_STYLE = {
  sold_out: { bg: "rgba(239,68,68,0.14)", fg: "#ef4444" },
  almost: { bg: "rgba(249,115,22,0.14)", fg: "#f97316" },
  high: { bg: "rgba(245,158,11,0.16)", fg: "#b45309" },
  available: { bg: "rgba(34,197,94,0.14)", fg: "#16a34a" },
  unknown: { bg: "rgba(148,163,184,0.14)", fg: "#64748b" },
};

const PRESSURE_STYLE = (idx) =>
  idx == null ? { bg: "rgba(148,163,184,0.14)", fg: "#64748b" }
  : idx >= 80 ? { bg: "rgba(239,68,68,0.14)", fg: "#ef4444" }
  : idx >= 60 ? { bg: "rgba(249,115,22,0.14)", fg: "#f97316" }
  : idx >= 40 ? { bg: "rgba(245,158,11,0.16)", fg: "#b45309" }
  : { bg: "rgba(34,197,94,0.14)", fg: "#16a34a" };

const influenceColor = (v) =>
  v == null ? "#64748b" : v >= 66 ? "#16a34a" : v >= 40 ? "#b45309" : "#94a3b8";

export default function CompetitorIntelligence({ propId, lang = "it", date }) {
  const L = (it, en) => (lang === "en" ? en : it);
  const nav = useNavigate();
  const openDetail = (cid) => {
    nav(`/competitor?prop=${propId}&cid=${encodeURIComponent(cid)}&stay=${date || ""}`);
  };
  const [ov, setOv] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [manage, setManage] = useState(false);
  const [detected, setDetected] = useState(null);
  const [sort, setSort] = useState("influence");
  const [q, setQ] = useState("");
  const [usageTick, setUsageTick] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const pollRef = useRef(null);

  const loadOverview = useCallback(async () => {
    if (!propId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/properties/${propId}/market/overview?date=${date || ""}&lang=${lang}`);
      setOv(data);
    } catch { setOv(null); }
    finally { setLoading(false); }
  }, [propId, date, lang]);

  const loadDetected = useCallback(async () => {
    if (!propId) return;
    try { const { data } = await api.get(`/properties/${propId}/competitors/detected`); setDetected(data); }
    catch { setDetected(null); }
  }, [propId]);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // The ONLY user-facing Apify trigger on the dashboard. Aggiorna il mercato SOLO per
  // oggi + i prossimi 7 giorni (niente griglia 6 mesi). Cattura anche il tuo prezzo.
  // Conferma tramite AlertDialog in-app (window.confirm è bloccato nell'iframe preview).
  const doRefresh = async () => {
    setConfirmOpen(false);
    if (refreshing) return;
    setRefreshing(true);
    try {
      const { data } = await api.post(`/properties/${propId}/market/run-scheduler`, { mode: "window" });
      toast.success(L(
        `Scansione avviata (oggi → +7 giorni, ~${data.est_runs} chiamate Apify, costo stimato ~$${data.est_cost_usd}).`,
        `Scan started (today → +7 days, ~${data.est_runs} Apify calls, est. cost ~$${data.est_cost_usd}).`),
        { description: L("I risultati (mercato + tuo prezzo) compaiono tra qualche secondo.", "Results (market + your price) appear in a few seconds.") });
      let tries = 0;
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = setInterval(async () => {
        tries++;
        await loadOverview();
        setUsageTick((t) => t + 1);
        if (tries >= 10) { clearInterval(pollRef.current); pollRef.current = null; setRefreshing(false); }
      }, 5000);
    } catch (e) { setRefreshing(false); toast.error(e?.response?.data?.detail || L("Errore aggiornamento", "Refresh error")); }
  };

  const saveMonitored = async (monitored_ids, blocked_ids) => {
    try {
      const body = {}; if (monitored_ids) body.monitored_ids = monitored_ids; if (blocked_ids) body.blocked_ids = blocked_ids;
      await api.post(`/properties/${propId}/competitors/monitored`, body);
      await loadDetected(); await loadOverview();
    } catch (e) { toast.error(e?.response?.data?.detail || L("Errore salvataggio", "Save error")); }
  };

  const toggleMonitor = (cid) => {
    if (!detected) return;
    const cur = detected.monitored_ids || [];
    if (cur.includes(cid)) saveMonitored(cur.filter((x) => x !== cid), null);
    else { if (cur.length >= 5) { toast.error(L("Hai già 5 competitor. Rimuovine uno prima di aggiungerne un altro.", "You already have 5 competitors. Remove one before adding another.")); return; } saveMonitored([...cur, cid], null); }
  };
  const blockComp = (cid) => {
    if (!detected) return;
    const b = new Set(detected.blocked_ids || []); b.add(cid);
    saveMonitored(null, Array.from(b));
  };

  const restoreSuggested = async () => {
    try {
      await api.post(`/properties/${propId}/competitors/restore-suggested`, { lang });
      await loadDetected(); await loadOverview();
      toast.success(L("Ripristinati i competitor diretti suggeriti dall'AI", "Restored the AI-suggested direct competitors"));
    } catch (e) { toast.error(e?.response?.data?.detail || L("Errore ripristino", "Restore error")); }
  };

  const applySuggestion = async (cid, action) => {
    try {
      await api.post(`/properties/${propId}/competitors/apply-suggestion`, { competitor_id: cid, action, lang });
      await loadDetected(); await loadOverview();
      if (action === "accept") toast.success(L("Suggerimento applicato", "Suggestion applied"));
    } catch (e) { toast.error(e?.response?.data?.detail || L("Errore", "Error")); }
  };

  const runScheduler = async () => {
    if (!window.confirm(L(
      "Questa scansione legge il mercato su ~43 date chiave (prossime settimane, weekend, festivi) e usa fino a ~43 chiamate Apify (costo ~$2-3). Aggiorna anche il prezzo della tua struttura, senza costi extra, per le date trovate. Continuare?",
      "This scan reads the market on ~43 key dates (coming weeks, weekends, holidays) using up to ~43 Apify calls (cost ~$2-3). It also updates your own property price for the dates found, at no extra cost. Continue?"))) return;
    try {
      const { data } = await api.post(`/properties/${propId}/market/run-scheduler`, { mode: "nightly" });
      toast.success(L(`Scansione avviata (${data.dates_planned} date, ~${data.est_runs} chiamate Apify, costo stimato ~$${data.est_cost_usd}). Aggiorna anche il tuo prezzo per le date trovate.`, `Scan started (${data.dates_planned} dates, ~${data.est_runs} Apify calls, est. cost ~$${data.est_cost_usd}). It also refreshes your own price for the dates found.`));
    } catch (e) { toast.error(e?.response?.data?.detail || L("Errore avvio scansione", "Scan start error")); }
  };

  const fmtDate = (iso) => { try { const [y, m, d] = iso.split("-"); return `${d}/${m}/${y}`; } catch { return iso; } };
  const fmtDT = (iso) => { try { const dt = new Date(iso); return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")} ${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`; } catch { return "—"; } };

  const refDate = date || ov?.stay_date;
  const mp = ov?.market_pressure;
  const rows = (ov?.monitored || []).slice();
  rows.sort((a, b) => {
    if (sort === "influence") return (b.market_influence_score || 0) - (a.market_influence_score || 0);
    if (sort === "similarity") return ((b.similarity_score ?? b.similarity) || 0) - ((a.similarity_score ?? a.similarity) || 0);
    if (sort === "occupancy") return (b.occupancy_est || 0) - (a.occupancy_est || 0);
    if (sort === "distance") return (a.distance_km || 999) - (b.distance_km || 999);
    return (b.price || 0) - (a.price || 0);
  });
  const filtered = q ? rows.filter((r) => (r.name || "").toLowerCase().includes(q.toLowerCase())) : rows;

  const Header = () => (
    <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
      <div className="flex items-center gap-2"><Users className="w-5 h-5 text-primary" /><h3 className="text-lg font-semibold">{L("Intelligence Competitor", "Competitor Intelligence")}</h3></div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-primary/10 text-primary font-medium" data-testid="ci-ref-date" title={L("La data si imposta dal pannello Consiglio Prezzo", "Date is set from the Price Advisor")}>
          <CalendarDays className="w-3.5 h-3.5" />{L("Soggiorno", "Stay")} {refDate ? fmtDate(refDate) : "—"}
        </span>
        {ov?.last_updated && (
          <span className="inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full bg-muted text-muted-foreground" data-testid="ci-last-updated" title={L("Fonte", "Source") + ": " + (ov.source || "—")}>
            <Clock className="w-3.5 h-3.5" />{L("Aggiornato", "Updated")} {fmtDT(ov.last_updated)} · <Database className="w-3 h-3" />{ov.source || "—"}
          </span>
        )}
        {ov?.needs_refresh && (
          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full" style={{ backgroundColor: "rgba(245,158,11,0.16)", color: "#b45309" }} data-testid="ci-needs-refresh">
            <AlertTriangle className="w-3.5 h-3.5" />{ov.has_data ? L("Dati non recenti", "Data not recent") : L("Nessun dato", "No data")}
          </span>
        )}
        <Button data-testid="ci-refresh-btn" size="sm" variant="outline" onClick={() => setConfirmOpen(true)} disabled={refreshing || !ov?.has_booking_url} title={L("Aggiorna il mercato per oggi e i prossimi 7 giorni (cattura anche il tuo prezzo)", "Refresh the market for today and the next 7 days (also captures your own price)")}>
          {refreshing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}{L("Aggiorna mercato", "Refresh market")}
        </Button>
        <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
          <AlertDialogContent data-testid="ci-refresh-confirm">
            <AlertDialogHeader>
              <AlertDialogTitle>{L("Aggiornare il mercato?", "Refresh the market?")}</AlertDialogTitle>
              <AlertDialogDescription>
                {L("Aggiorna il mercato per OGGI e i prossimi 7 giorni (fino a ~8 chiamate Apify). Aggiorna anche il prezzo della tua struttura, senza costi extra.", "Refresh the market for TODAY and the next 7 days (up to ~8 Apify calls). It also refreshes your own property price, at no extra cost.")}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="ci-refresh-cancel">{L("Annulla", "Cancel")}</AlertDialogCancel>
              <AlertDialogAction data-testid="ci-refresh-confirm-btn" onClick={doRefresh}>{L("Aggiorna ora", "Refresh now")}</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <Button data-testid="ci-manage-btn" size="sm" variant="ghost" onClick={() => { setManage((m) => !m); if (!detected) loadDetected(); }}>
          <Settings2 className="w-4 h-4 mr-1.5" />{L("Gestisci", "Manage")}
        </Button>
      </div>
    </div>
  );

  return (
    <motion.div data-testid="competitor-intelligence" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="md:col-span-4 bg-card border border-border rounded-lg p-6">
      <Header />
      <MarketUpdateControls lang={lang} lastUpdated={ov?.last_updated} source={ov?.source} ageHours={ov?.age_hours} refreshTick={usageTick} />
      <p className="text-[11px] text-muted-foreground mb-3">{L("La dashboard legge dal database di mercato condiviso della destinazione. Apify viene interrogato solo con \"Aggiorna mercato\" (oggi + prossimi 7 giorni) o dagli aggiornamenti automatici programmati.", "The dashboard reads from the shared destination market database. Apify is only queried via \"Refresh market\" (today + next 7 days) or the scheduled automatic updates.")}</p>


      {manage && (
        <div className="mb-4 border border-border rounded-lg p-4 bg-muted/30" data-testid="ci-manage-panel">
          <div className="flex items-center justify-between mb-2">
            <h4 className="text-sm font-semibold">{L("Competitor rilevati", "Detected competitors")} <span className="text-muted-foreground font-normal">({detected?.detected?.length || 0}) · {L("monitorati", "monitored")} {detected?.monitored_ids?.length || 0}/5</span></h4>
            <div className="flex items-center gap-2">
              <Button data-testid="ci-restore-suggested" size="sm" variant="ghost" onClick={restoreSuggested} disabled={refreshing || !(detected?.detected?.length)} title={L("Ripristina i 5 competitor diretti suggeriti dall'AI (per similarità)", "Restore the 5 AI-suggested direct competitors (by similarity)")}>
                <Sparkles className="w-4 h-4 mr-1.5" />{L("5 suggeriti AI", "AI top 5")}
              </Button>
              <Button data-testid="ci-run-scheduler" size="sm" variant="outline" onClick={runScheduler} disabled={refreshing} title={L("Scansione notturna della griglia intelligente (settimana, weekend, festivi, +1..+6 mesi)", "Nightly smart-grid scan (week, weekends, holidays, +1..+6 months)")}>
                <CalendarDays className="w-4 h-4 mr-1.5" />{L("Scansiona griglia", "Scan grid")}
              </Button>
            </div>
          </div>
          {(detected?.suggestions || []).length > 0 && (
            <div className="mb-3 rounded-lg border border-amber-300/60 bg-amber-50/60 dark:bg-amber-950/20 p-3" data-testid="ci-suggestions">
              <div className="flex items-center gap-1.5 mb-2 text-amber-700 dark:text-amber-400">
                <Sparkles className="w-4 h-4" />
                <span className="text-xs font-semibold">{L("Proposte AI (non applicate automaticamente)", "AI proposals (not applied automatically)")}</span>
              </div>
              <div className="space-y-2">
                {(detected?.suggestions || []).map((s, i) => (
                  <div key={s.competitor_id} data-testid={`ci-suggestion-${i}`} className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-xs font-medium truncate">{s.name}{s.similarity ? <span className="text-muted-foreground font-normal"> · {s.similarity}%</span> : null}</div>
                      <div className="text-[11px] text-muted-foreground">{s.reason_it}</div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {s.type !== "review" && (
                        <button data-testid={`ci-suggestion-accept-${i}`} onClick={() => applySuggestion(s.competitor_id, "accept")} className="text-xs px-2 py-1 rounded-full bg-primary text-primary-foreground">{L("Accetta", "Accept")}</button>
                      )}
                      <button data-testid={`ci-suggestion-dismiss-${i}`} onClick={() => applySuggestion(s.competitor_id, "dismiss")} className="text-xs px-2 py-1 rounded-full bg-accent text-foreground">{L("Ignora", "Dismiss")}</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="max-h-64 overflow-auto divide-y divide-border">
            {(detected?.detected || []).filter((c) => !c.blocked).map((c, i) => (
              <div key={c.competitor_id} data-testid={`ci-detected-${i}`} className="flex items-center justify-between py-2 gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{c.name}</div>
                  <div className="text-[11px] text-muted-foreground">{c.category || "—"} · {c.distance_km != null ? `${c.distance_km} km` : "—"}{c.rating ? ` · ★ ${c.rating}` : ""}{c.total_rooms ? ` · ${c.total_rooms} ${L("camere", "rooms")}` : ""}</div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button data-testid={`ci-monitor-toggle-${i}`} onClick={() => toggleMonitor(c.competitor_id)} className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${c.monitored ? "bg-primary text-primary-foreground" : "bg-accent text-foreground"}`}>
                    {c.monitored ? <Star className="w-3.5 h-3.5 fill-current" /> : <Plus className="w-3.5 h-3.5" />}{c.monitored ? L("Monitorato", "Monitored") : L("Aggiungi", "Add")}
                  </button>
                  <button data-testid={`ci-block-${i}`} onClick={() => blockComp(c.competitor_id)} title={L("Blocca (non ricomparirà)", "Block (won't reappear)")} className="text-muted-foreground hover:text-red-500 p-1"><X className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {loading && !ov ? (
        <div className="py-8 text-center text-muted-foreground text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />{L("Carico dal database…", "Loading from database…")}</div>
      ) : !ov?.has_data ? (
        <div className="py-8 text-center" data-testid="ci-empty">
          <p className="text-sm text-muted-foreground mb-3">{L("Nessun dato di mercato per questa destinazione. Avvia il primo rilevamento.", "No market data for this destination yet. Run the first detection.")}</p>
          {ov?.has_booking_url && <Button data-testid="ci-first-scan" onClick={() => setConfirmOpen(true)} disabled={refreshing}>{refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}{L("Rileva ora", "Detect now")}</Button>}
        </div>
      ) : (
        <div className="overflow-x-auto">
          {mp && mp.index != null && (
            <div data-testid="ci-market-pressure" className="mb-4 rounded-lg border border-border p-4 bg-muted/20">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs uppercase tracking-widest font-bold text-muted-foreground">{L("Pressione di mercato (pesata per camere)", "Market pressure (room-weighted)")}</span>
                  <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ backgroundColor: PRESSURE_STYLE(mp.index).bg, color: PRESSURE_STYLE(mp.index).fg }} data-testid="ci-pressure-badge">{mp.label} · {mp.index}/100</span>
                </div>
                <div className="flex items-center gap-4 text-xs text-muted-foreground flex-wrap">
                  <span>{L("Camere totali", "Total rooms")}: <b className="text-foreground font-data">{mp.total_rooms}</b></span>
                  <span>{L("Disponibili", "Available")}: <b className="text-foreground font-data">{mp.available_rooms}</b></span>
                  <span>{L("Occupate", "Occupied")}: <b className="text-foreground font-data">{mp.occupied_rooms}</b></span>
                  <span>{L("Occupazione", "Occupancy")}: <b className="text-foreground font-data">{mp.market_occupancy_pct != null ? `${mp.market_occupancy_pct}%` : "—"}</b></span>
                  <span>{L("Capacità residua", "Residual capacity")}: <b className="text-foreground font-data">{mp.residual_capacity_pct != null ? `${mp.residual_capacity_pct}%` : "—"}</b></span>
                </div>
              </div>
              <p className="text-[11px] text-muted-foreground mt-2">{L("2 camere sold out non pesano come 20: la pressione è calcolata sul numero di camere e sulla similarità di ogni competitor.", "2 sold-out rooms don't weigh like 20: pressure is computed on each competitor's room count and similarity.")}</p>
            </div>
          )}
          <div className="flex items-center gap-2 mb-2">
            <Input data-testid="ci-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={L("Cerca competitor…", "Search competitor…")} className="h-8 max-w-[220px] text-xs" />
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="ci-sort" className="h-8 w-[170px] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="influence">{L("Influenza", "Influence")}</SelectItem>
                <SelectItem value="similarity">{L("Similarità", "Similarity")}</SelectItem>
                <SelectItem value="price">{L("Prezzo", "Price")}</SelectItem>
                <SelectItem value="occupancy">{L("Occupazione", "Occupancy")}</SelectItem>
                <SelectItem value="distance">{L("Distanza", "Distance")}</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-[11px] text-muted-foreground inline-flex items-center gap-2 ml-1">
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-primary" />{L("Prezzo", "Price")}</span>
              <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: "#64748b" }} />{L("Mercato", "Market")}</span>
            </span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground border-b border-border">
                <th className="px-2 py-2">{L("Struttura", "Property")}</th>
                <th className="px-2 py-2 text-center" title={L("Similarità con la tua struttura", "Similarity to your property")}>{L("Sim.", "Sim.")}</th>
                <th className="px-2 py-2 text-center" title={L("Market Influence Score", "Market Influence Score")}>{L("Influenza", "Influence")}</th>
                <th className="px-2 py-2 text-center" title={L("Peso usato nel motore di pricing", "Weight used in the pricing engine")}>{L("Peso", "Weight")}</th>
                <th className="px-2 py-2">{L("Dist.", "Dist.")}</th>
                <th className="px-2 py-2 text-right">{L("Lordo", "Gross")}</th>
                <th className="px-2 py-2 text-right" title={L("Prezzo netto stimato (tua calibrazione)", "Estimated net price (your calibration)")}>{L("Netto", "Net")}</th>
                <th className="px-2 py-2 text-center">{L("Camere", "Rooms")}</th>
                <th className="px-2 py-2 text-right">{L("Occ.", "Occ.")}</th>
                <th className="px-2 py-2">{L("Stato", "Status")}</th>
                <th className="px-2 py-2 text-right" title={L("Affidabilità dei dati", "Data reliability")}>{L("Affid.", "Rel.")}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r, i) => {
                const st = STATUS_STYLE[(r.status || {}).key] || STATUS_STYLE.unknown;
                const pd = r.price_delta;
                const isPrice = r.comp_type === "price";
                const rel = r.reliability_score != null ? r.reliability_score : r.confidence;
                return (
                  <tr key={r.competitor_id} data-testid={`ci-row-${i}`} onClick={() => openDetail(r.competitor_id)} className="border-t border-border hover:bg-accent/30 cursor-pointer" title={L("Apri dettaglio competitor", "Open competitor detail")}>
                    <td className="px-2 py-2">
                      <div className="font-medium truncate max-w-[190px] text-primary hover:underline">{r.name}</div>
                      <div className="text-[10px] flex items-center gap-1.5">
                        {r.comp_type && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full font-semibold" data-testid={`ci-type-${i}`}
                            style={isPrice ? { background: "hsl(var(--primary)/0.12)", color: "hsl(var(--primary))" } : { background: "rgba(100,116,139,0.14)", color: "#64748b" }}>
                            {isPrice ? L("Prezzo", "Price") : L("Mercato", "Market")}
                          </span>
                        )}
                        {r.pickup_role === "leader" && <span className="inline-flex items-center gap-0.5 text-[9px] font-bold text-amber-600"><Zap className="w-3 h-3" />{L("Leader", "Leader")}</span>}
                        <span className="text-muted-foreground truncate">{r.category || r.segment || "—"}</span>
                      </div>
                    </td>
                    <td className="px-2 py-2 text-center font-data" data-testid={`ci-sim-${i}`}>{r.similarity_score != null ? `${r.similarity_score}` : (r.similarity != null ? `${r.similarity}` : "—")}</td>
                    <td className="px-2 py-2 text-center" data-testid={`ci-influence-${i}`}>
                      {r.market_influence_score != null ? (
                        <span className="font-data font-semibold" style={{ color: influenceColor(r.market_influence_score) }}>{r.market_influence_score}</span>
                      ) : "—"}
                    </td>
                    <td className="px-2 py-2 text-center font-data text-xs" data-testid={`ci-weight-${i}`}>{r.pricing_weight != null ? `${Math.round(r.pricing_weight * 100)}%` : "—"}</td>
                    <td className="px-2 py-2 text-muted-foreground text-xs">{r.distance_km != null ? `${r.distance_km} km` : "—"}</td>
                    <td className="px-2 py-2 text-right font-data" data-testid={`ci-price-${i}`}>
                      {r.price != null ? (
                        <>€{Math.round(r.price)}{pd != null && pd !== 0 && <span className="ml-1 inline-flex items-center text-[10px]" style={{ color: pd > 0 ? "#ef4444" : "#16a34a" }}>{pd > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}{pd > 0 ? "+" : ""}{pd}</span>}</>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: STATUS_STYLE.sold_out.bg, color: STATUS_STYLE.sold_out.fg }}>SOLD OUT</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right font-data font-semibold text-primary" data-testid={`ci-net-price-${i}`}>
                      {r.net_price != null ? `€${Math.round(r.net_price)}` : "—"}
                    </td>
                    <td className="px-2 py-2 text-center font-data">
                      {r.rooms_available != null ? `${r.rooms_available}/${r.total_rooms || "?"}` : "—"}
                      {r.availability_delta != null && r.availability_delta !== 0 && <span className="ml-1 text-[10px]" style={{ color: r.availability_delta < 0 ? "#ef4444" : "#16a34a" }}>{r.availability_delta > 0 ? "+" : ""}{r.availability_delta}</span>}
                    </td>
                    <td className="px-2 py-2 text-right font-data">{r.occupancy_est != null ? `${r.occupancy_est}%` : "—"}</td>
                    <td className="px-2 py-2"><span className="text-[11px] font-medium px-2 py-0.5 rounded-full" style={{ backgroundColor: st.bg, color: st.fg }}>{(r.status || {}).label || "—"}</span></td>
                    <td className="px-2 py-2 text-right text-xs text-muted-foreground" data-testid={`ci-rel-${i}`}>{rel != null ? `${rel}%` : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </motion.div>
  );
}
